// DESIGN-SOURCE: none — not in Figma — SE-7324: covered-share field requested from design (Miguel); no new style values, mirrors the Duration field
/**
 * Modal dialog for setting a Loan action on a player in scenario mode.
 *
 * Shows Loan Fee (MoneyInput), Duration (months) and Covered Share (%)
 * inputs. Supports create mode (Cancel + Confirm) and edit mode (Cancel Loan
 * + Close + Update). Confirm/Update stays disabled until the fee, the
 * duration and — when required — a covered share in 0–100 are all present.
 * @module features/squad-planning/components/LoanActionModal
 */

import React, { memo } from "react";

import LiveImpactPanel from "@/features/financial-compliance/components/molecules/LiveImpactPanel";
import MoneyInput from "@/components/molecules/MoneyInput";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Heading } from "@/components/atoms/Heading";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/atoms/Modal";
import { Text } from "@/components/atoms/Text";
import { TextInput } from "@/components/atoms/TextInput";

import type { LiveImpactModel } from "@/features/financial-compliance/types";

/** Props for {@link LoanActionModalView} */
export interface LoanActionModalViewProps {
  /** Whether the modal is open */
  readonly isOpen: boolean;
  /** Player name for the title */
  readonly playerName: string;
  /** Current loan fee value in raw units */
  readonly loanFee: number | null;
  /** Current duration in months as string */
  readonly durationText: string;
  /** Share of the wage the borrowing club covers (0–100), as typed */
  readonly coveredShareText: string;
  /** Inline validation message for the covered share, or null when valid */
  readonly coveredShareError: string | null;
  /**
   * Whether Confirm/Update needs a covered share. False only for a legacy
   * loan that never recorded one (SE-7324).
   */
  readonly isCoveredShareRequired: boolean;
  /** Called when loan fee changes via MoneyInput typing and confirm */
  readonly onLoanFeeChange: (value: number | null) => void;
  /** Called when duration text changes */
  readonly onDurationChange: (text: string) => void;
  /** Called when the covered share text changes */
  readonly onCoveredShareChange: (text: string) => void;
  /** Called on confirm/update */
  readonly onConfirm: () => void;
  /** Called on cancel/close */
  readonly onCancel: () => void;
  /** Called to remove the action (edit mode only) */
  readonly onCancelAction?: () => void;
  /** Whether the modal is in edit mode */
  readonly isEditMode?: boolean;
  /** Live Impact verdict for the scenario after this action (SE-7002) */
  readonly liveImpact: LiveImpactModel;
  /** Opens the full impact view for the current scenario */
  readonly onViewFullImpact: () => void;
}

/**
 * Whether the covered share blocks Confirm/Update.
 * @param text - Covered share as typed
 * @param error - Inline validation message, or null when in range
 * @param isRequired - Whether a blank share blocks confirmation
 * @returns True when the share alone should keep the button disabled
 */
const isCoveredShareBlocking = (
  text: string,
  error: string | null,
  isRequired: boolean
): boolean => (isRequired && text.trim() === "") || error != null;

/**
 * Renders the Loan action modal with Loan Fee, Duration and Covered Share inputs.
 * @param props - Component props
 * @param props.isOpen - Whether the modal is visible
 * @param props.playerName - Player name shown in the heading
 * @param props.loanFee - Current loan fee in raw units
 * @param props.durationText - Duration in months as text string
 * @param props.coveredShareText - Covered share percentage as typed
 * @param props.coveredShareError - Inline validation message, or null
 * @param props.isCoveredShareRequired - Whether a blank share blocks confirmation
 * @param props.onLoanFeeChange - Handler for loan fee updates
 * @param props.onDurationChange - Handler for duration text changes
 * @param props.onCoveredShareChange - Handler for covered share changes
 * @param props.onConfirm - Handler for confirm/update button
 * @param props.onCancel - Handler for cancel/close button and backdrop
 * @param props.onCancelAction - Handler for "Cancel Loan" link (edit mode)
 * @param props.isEditMode - Whether in edit mode
 * @param props.liveImpact - Live Impact verdict for the whole scenario
 * @param props.onViewFullImpact - Opens the full impact view
 */
const LoanActionModalView = ({
  isOpen,
  playerName,
  loanFee,
  durationText,
  coveredShareText,
  coveredShareError,
  isCoveredShareRequired,
  onLoanFeeChange,
  onDurationChange,
  onCoveredShareChange,
  onConfirm,
  onCancel,
  onCancelAction,
  isEditMode = false,
  liveImpact,
  onViewFullImpact,
}: LoanActionModalViewProps) => (
  <Modal isOpen={isOpen} onClose={onCancel} size="md" avoidKeyboard>
    <ModalBackdrop />
    <ModalContent className="p-6">
      <ModalHeader>
        <Heading variant="title-lg">
          Loan out{" "}
          <Text variant="title" className="text-accent-primary">
            {playerName}
          </Text>
        </Heading>
      </ModalHeader>
      <ModalBody>
        <Stack className="gap-4">
          <Stack className="gap-1">
            <Text variant="body-strong" className="text-content-muted">
              Loan Fee (millions)
            </Text>
            <MoneyInput
              value={loanFee}
              onConfirm={onLoanFeeChange}
              onChange={onLoanFeeChange}
              onCancel={onCancel}
              variant="expanded"
            />
          </Stack>
          <Stack className="gap-1">
            <Text variant="body-strong" className="text-content-muted">
              Duration (months)
            </Text>
            <TextInput
              testID="loan-duration-input"
              className="web:outline-none web:focus:border-accent-primary h-9 w-full rounded-sm border border-outline-default px-3 py-2 text-body text-content-primary"
              value={durationText}
              onChangeText={onDurationChange}
              placeholder="12"
              placeholderTextColor="#a3a3a3"
              keyboardType="number-pad"
            />
          </Stack>
          <Stack className="gap-1">
            <Text variant="body-strong" className="text-content-muted">
              Covered Share (%)
            </Text>
            <Text variant="caption" className="text-content-muted">
              Share of the wage the borrowing club covers during the loan. 100 =
              they pay all of it.
            </Text>
            <TextInput
              testID="loan-covered-share-input"
              className="web:outline-none web:focus:border-accent-primary h-9 w-full rounded-sm border border-outline-default px-3 py-2 text-body text-content-primary"
              value={coveredShareText}
              onChangeText={onCoveredShareChange}
              placeholder="0–100"
              placeholderTextColor="#a3a3a3"
              keyboardType="number-pad"
            />
            {coveredShareError != null ? (
              <Text variant="caption" className="text-status-error">
                {coveredShareError}
              </Text>
            ) : null}
          </Stack>
          <LiveImpactPanel
            testID="loan-live-impact"
            model={liveImpact}
            onViewFullImpact={onViewFullImpact}
          />
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Stack
          direction="horizontal"
          className="flex-1 flex-wrap items-center justify-between gap-2"
        >
          {isEditMode && onCancelAction != null ? (
            <Button
              variant="link"
              onPress={onCancelAction}
              size="md"
              label="Cancel Loan"
              labelTone="danger"
            />
          ) : (
            <Stack direction="horizontal" />
          )}
          <Stack direction="horizontal" className="gap-4">
            <Button
              variant="outline"
              onPress={onCancel}
              size="md"
              className="border-outline-strong"
              label={isEditMode ? "Close" : "Cancel"}
              labelTone="primary"
            />
            <Button
              testID="loan-confirm-button"
              onPress={onConfirm}
              size="md"
              className="bg-accent-primary"
              isDisabled={
                loanFee == null ||
                loanFee <= 0 ||
                !durationText.trim() ||
                parseInt(durationText, 10) <= 0 ||
                isCoveredShareBlocking(
                  coveredShareText,
                  coveredShareError,
                  isCoveredShareRequired
                )
              }
              label={isEditMode ? "Update" : "Confirm"}
              labelTone="on-accent"
            />
          </Stack>
        </Stack>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

LoanActionModalView.displayName = "LoanActionModalView";

export default memo(LoanActionModalView);
